import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";

import { toast } from "react-toastify";
import * as Yup from "yup";
import { isEmpty } from "lodash";
import {
  changePassword,
  changePasswordSuccess,
} from "../../../../../store/auth/forgetpwd/actions";

const ChangePasswordLoggedIn = ({ page }) => {
  const dispatch = useDispatch();

  const [values, setValues] = useState({
    old_password: "",
    password: "",
    confirm_password: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [showSuccessView, setShowSuccessView] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  const { changePasswordSuccessAPI, changePasswordSuccessLoading } =
    useSelector((state) => ({
      changePasswordSuccessAPI: state.ForgetPassword.changePasswordSuccess,
      changePasswordSuccessLoading:
        state.ForgetPassword.changePasswordSuccessLoading,
    }));

  const schema = Yup.object().shape({
    old_password: Yup.string().required("Old password is required"),
    password: Yup.string()
      .required("New password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[a-z]/, "Must contain a lowercase letter")
      .matches(/[A-Z]/, "Must contain an uppercase letter")
      .matches(/[0-9]/, "Must contain a number"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Confirm password is required"),
  });

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: undefined });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await schema.validate(values, { abortEarly: false });
      const payload = [
        {
          PasswordOld: values.old_password,
          PasswordNew: values.password,
          PasswordNewVerify: values.confirm_password,
        },
      ];
      dispatch(changePassword(payload));
    } catch (validationErrors) {
      const formattedErrors = {};
      validationErrors.inner.forEach((err) => {
        formattedErrors[err.path] = err.message;
      });
      setErrors(formattedErrors);
    }
  };

  useEffect(() => {
    if (isEmpty(changePasswordSuccessAPI)) return;

    if (changePasswordSuccessAPI?.status === 200) {
      toast.success(changePasswordSuccessAPI?.message);
      setValues({ old_password: "", password: "", confirm_password: "" });
      setShowSuccessView(true);
      setFeedbackMessage(null);
    } else {
      toast.error(changePasswordSuccessAPI?.message);
      setFeedbackMessage(
        changePasswordSuccessAPI?.message || "Something went wrong."
      );
    }

    dispatch(changePasswordSuccess([]));
  }, [changePasswordSuccessAPI]);
  const oldRef = useRef();
  const newRef = useRef();
  const confirmRef = useRef();

  return (
    <Container maxWidth="sm">
      <Card sx={{ mt: 8 }}>
        <CardContent>
          <Typography variant="h5" align="center" gutterBottom>
            Change Password
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            gutterBottom
          >
            Your new password must be different from previously used passwords.
          </Typography>
          {showSuccessView ? (
            <>
              <Typography variant="h6" align="center" sx={{ mt: 3 }}>
                Password Updated
              </Typography>
              <Typography align="center" sx={{ mt: 1 }}>
                {changePasswordSuccessAPI?.message ||
                  "Your password has been changed successfully."}
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => setShowSuccessView(false)}
                >
                  Change Password Again?
                </Button>
              </Box>
            </>
          ) : (
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 2 }}
            >
              <TextField
                inputRef={oldRef}
                onKeyDown={(e) => {
                  if (e.key === "Tab" && !e.shiftKey) {
                    e.preventDefault();
                    newRef.current.focus();
                  }
                }}
                fullWidth
                margin="normal"
                label="Old Password"
                type={showPassword.old ? "text" : "password"}
                name="old_password"
                value={values.old_password}
                onChange={handleChange}
                error={!!errors.old_password}
                helperText={errors.old_password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPassword((prev) => ({
                            ...prev,
                            old: !prev.old,
                          }))
                        }
                      >
                        {showPassword.old ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                inputRef={newRef}
                onKeyDown={(e) => {
                  if (e.key === "Tab" && !e.shiftKey) {
                    e.preventDefault();
                    confirmRef.current.focus();
                  }
                  if (e.key === "Tab" && e.shiftKey) {
                    e.preventDefault();
                    oldRef.current.focus();
                  }
                }}
                fullWidth
                margin="normal"
                label="New Password"
                type={showPassword.new ? "text" : "password"}
                name="password"
                value={values.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPassword((prev) => ({
                            ...prev,
                            new: !prev.new,
                          }))
                        }
                      >
                        {showPassword.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                inputRef={confirmRef}
                onKeyDown={(e) => {
                  if (e.key === "Tab" && e.shiftKey) {
                    e.preventDefault();
                    newRef.current.focus();
                  }
                }}
                fullWidth
                margin="normal"
                label="Confirm Password"
                type={showPassword.confirm ? "text" : "password"}
                name="confirm_password"
                value={values.confirm_password}
                onChange={handleChange}
                error={!!errors.confirm_password}
                helperText={errors.confirm_password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPassword((prev) => ({
                            ...prev,
                            confirm: !prev.confirm,
                          }))
                        }
                      >
                        {showPassword.confirm ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {feedbackMessage && (
                <Box
                  sx={{
                    backgroundColor: "#ffe6e6",
                    border: "1px solid #ffcccc",
                    borderRadius: 1,
                    padding: 2,
                    marginTop: 2,
                    color: "#cc0000",
                    textAlign: "center",
                  }}
                >
                  {feedbackMessage}
                </Box>
              )}
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                >
                  {"Change Password"}
                </Button>
              </Box>

              {!page && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ChangePasswordLoggedIn;
